import { PieChart } from '@mui/x-charts/PieChart'
import { Box, CircularProgress, Grid, Paper, Typography } from '@mui/material'
import { useGetVoucherChannelDistributionQuery } from 'store/dashboard'
import NoDataAvailable from './NoDataAvailable'
import { useTimeRange } from './TimeRangeContext'

const CHANNEL_PALETTE = [
  '#002C5F',
  '#4A90D9',
  '#20B2AA',
  '#F0A500',
  '#E57373',
  '#81C784',
  '#BA68C8',
  '#FF8A65'
]

const getChannelColor = (index: number): string => CHANNEL_PALETTE[index % CHANNEL_PALETTE.length]

const VoucherChannelDistributionChart = () => {
  const { startDate, endDate } = useTimeRange()
  const { data, isLoading, isFetching, isError, refetch } = useGetVoucherChannelDistributionQuery({
    startDate,
    endDate
  })

  const channels = data?.channels ?? []

  const renderContent = () => {
    if (isLoading || isFetching) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
          <CircularProgress />
        </Box>
      )
    }

    if (isError) {
      return <NoDataAvailable variant='error' onRefresh={refetch} />
    }
    if (!data) {
      return <NoDataAvailable onRefresh={refetch} />
    }

    if (channels.length === 0) {
      return <NoDataAvailable onRefresh={refetch} />
    }

    return (
      <Grid container spacing={2}>
        {channels.map((ch, index) => {
          const color = getChannelColor(index)
          const remainder = Math.max(0, 100 - ch.percentage)
          return (
            <Grid item xs={6} key={ch.channelCode}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  gap: 1.5,
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  bgcolor: '#fafafa'
                }}
              >
                <PieChart
                  series={[
                    {
                      data: [
                        { id: 0, value: ch.percentage, color },
                        { id: 1, value: remainder, color: '#E8E8E8' }
                      ],
                      innerRadius: 22,
                      outerRadius: 32,
                      cy: 32
                    }
                  ]}
                  width={10}
                  height={72}
                  slotProps={{ legend: { hidden: true } }}
                  sx={{ flex: 1 }}
                />
                <Box sx={{ flex: 3 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 22, color, lineHeight: 1.2 }}>
                    {ch.percentage}%
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{ color: 'text.secondary', fontSize: 12, mt: 0.5 }}
                  >
                    {ch.channelName}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )
        })}
      </Grid>
    )
  }

  return (
    <Paper
      sx={{
        p: 2,
        height: '100%',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        boxShadow: 'none'
      }}
      elevation={0}
    >
      {renderContent()}
    </Paper>
  )
}

export default VoucherChannelDistributionChart
